// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="FormDrawer" set="mytaible-shadcn-studio" verified=tbd
//
// Mobile  (< 640 px): Vaul Drawer — native swipe-to-dismiss, no touch hacks
// Desktop (≥ 640 px): Radix Sheet from the right — keyboard / mouse

import { Check, Trash2, X } from 'lucide-react';
import * as React from 'react';
import { Drawer as DrawerPrimitive } from 'vaul';
import { useMediaQuery } from '../../hooks/use-media-query';
import { cn } from '../../lib/utils';
import { Spinner } from '../atoms/spinner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './sheet';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children: React.ReactNode;
  onSubmit: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  isPending?: boolean;
  submitDisabled?: boolean;
  /**
   * When explicitly false: save button renders as muted/disabled (no unsaved changes).
   * When true or undefined: save button renders as success/green (ready to save).
   */
  isDirty?: boolean;
  className?: string;
  /** Custom icon for the submit button (defaults to Check) */
  submitIcon?: React.ReactNode;
  /** Called when Cancel is clicked — defaults to closing the drawer */
  onCancel?: () => void;
  /** When provided, renders a full-width delete button pinned at the bottom */
  onDelete?: () => void;
  deleteLabel?: string;
  isDeleting?: boolean;
  /** Extra icon buttons rendered in the header before save/close */
  headerActions?: React.ReactNode;
}

// ─── Shared header icon button ────────────────────────────────────────────────

interface HeaderIconButtonProps {
  onClick: () => void;
  disabled?: boolean;
  'aria-label': string;
  variant: 'save' | 'close';
  children: React.ReactNode;
}

export function DrawerHeaderButton({
  onClick,
  disabled,
  'aria-label': ariaLabel,
  variant,
  children,
}: HeaderIconButtonProps) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'flex items-center justify-center rounded-full w-8 h-8 cursor-pointer transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        'disabled:opacity-50 disabled:cursor-not-allowed shrink-0',
        variant === 'save'
          ? 'bg-success text-success-foreground hover:bg-success/90'
          : 'bg-muted text-muted-foreground hover:bg-muted/70',
      )}
    >
      {children}
    </button>
  );
}

// ─── Shared header ────────────────────────────────────────────────────────────

function FormDrawerHeader({
  title,
  headerActions,
  submitLabel,
  submitIcon,
  isDirty,
  isPending,
  submitDisabled,
  onSubmit,
  onClose,
}: {
  title: string;
  headerActions?: React.ReactNode;
  submitLabel: string;
  submitIcon?: React.ReactNode;
  isDirty?: boolean;
  isPending: boolean;
  submitDisabled: boolean;
  onSubmit: () => void;
  onClose: () => void;
}) {
  return (
    <div className="shrink-0 border-b border-border px-6 py-4 flex flex-row items-center justify-between">
      <span className="text-base font-semibold leading-none tracking-tight text-foreground">
        {title}
      </span>
      <div className="flex items-center gap-2">
        {headerActions}
        {isDirty !== false && (
          <DrawerHeaderButton
            aria-label={submitLabel}
            onClick={onSubmit}
            disabled={isPending || submitDisabled}
            variant="save"
          >
            {isPending ? (
              <Spinner size="sm" variant="success" />
            ) : (
              (submitIcon ?? <Check className="size-4" />)
            )}
          </DrawerHeaderButton>
        )}
        <DrawerHeaderButton
          aria-label="Close"
          onClick={onClose}
          disabled={isPending}
          variant="close"
        >
          <X className="size-4" />
        </DrawerHeaderButton>
      </div>
    </div>
  );
}

// ─── Shared delete footer ─────────────────────────────────────────────────────

function DeleteFooter({
  label,
  isDeleting,
  isPending,
  onDelete,
}: {
  label: string;
  isDeleting: boolean;
  isPending: boolean;
  onDelete: () => void;
}) {
  return (
    <div className="shrink-0 border-t border-border p-4">
      <button
        type="button"
        onClick={onDelete}
        disabled={isDeleting || isPending}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-destructive px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-destructive/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isDeleting ? <Spinner size="sm" /> : <Trash2 className="size-4" />}
        {label}
      </button>
    </div>
  );
}

// ─── FormDrawer ───────────────────────────────────────────────────────────────

/**
 * Responsive form panel:
 * - Mobile  (< 640 px): Vaul Drawer — native swipe-to-dismiss, slides up from bottom
 * - Desktop (≥ 640 px): Radix Sheet — slides in from the right
 *
 * Header: title (left) + save ✓ + close ✕ (right) — consistent on both.
 * Delete: full-width destructive button pinned at bottom (when onDelete provided).
 */
const FormDrawer = React.forwardRef<HTMLDivElement, FormDrawerProps>(
  (
    {
      open,
      onOpenChange,
      title,
      children,
      onSubmit,
      submitLabel = 'Save',
      cancelLabel = 'Cancel',
      isPending = false,
      submitDisabled = false,
      isDirty,
      className,
      submitIcon,
      onCancel,
      onDelete,
      deleteLabel = 'Delete',
      isDeleting = false,
      headerActions,
    },
    _ref,
  ) => {
    const isMobile = useMediaQuery('(max-width: 639px)');

    function handleClose() {
      onCancel ? onCancel() : onOpenChange(false);
    }

    const headerProps = {
      title,
      headerActions,
      submitLabel,
      submitIcon,
      isDirty,
      isPending,
      submitDisabled,
      onSubmit,
      onClose: handleClose,
    };

    // ── Mobile: Vaul Drawer ────────────────────────────────────────────────────
    if (isMobile) {
      return (
        <DrawerPrimitive.Root open={open} onOpenChange={onOpenChange}>
          <DrawerPrimitive.Portal>
            <DrawerPrimitive.Overlay className="fixed inset-0 z-50 bg-foreground/20 backdrop-blur-[3px]" />
            <DrawerPrimitive.Content
              className={cn(
                'fixed inset-x-0 bottom-0 z-50 flex flex-col bg-background',
                'rounded-t-[var(--card-radius,var(--radius))] border border-border',
                'h-[90svh]',
                className,
              )}
            >
              {/* Vaul drag handle — native gesture detection built in */}
              <div className="mx-auto mt-3 mb-1 h-1.5 w-10 shrink-0 rounded-full bg-muted-foreground/30" />

              <FormDrawerHeader {...headerProps} />

              <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">{children}</div>

              {onDelete && (
                <DeleteFooter
                  label={deleteLabel}
                  isDeleting={isDeleting}
                  isPending={isPending}
                  onDelete={onDelete}
                />
              )}
            </DrawerPrimitive.Content>
          </DrawerPrimitive.Portal>
        </DrawerPrimitive.Root>
      );
    }

    // ── Desktop: Radix Sheet (right panel) ────────────────────────────────────
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          hideCloseButton
          className={cn('flex flex-col gap-0 p-0 sm:max-w-md', className)}
          aria-describedby={undefined}
        >
          <SheetHeader className="shrink-0 border-b border-border px-6 py-4 flex flex-row items-center justify-between">
            <SheetTitle>{title}</SheetTitle>
            <div className="flex items-center gap-2">
              {headerActions}
              {isDirty !== false && (
                <DrawerHeaderButton
                  aria-label={submitLabel}
                  onClick={onSubmit}
                  disabled={isPending || submitDisabled}
                  variant="save"
                >
                  {isPending ? (
                    <Spinner size="sm" variant="success" />
                  ) : (
                    (submitIcon ?? <Check className="size-4" />)
                  )}
                </DrawerHeaderButton>
              )}
              <DrawerHeaderButton
                aria-label={cancelLabel}
                onClick={handleClose}
                disabled={isPending}
                variant="close"
              >
                <X className="size-4" />
              </DrawerHeaderButton>
            </div>
          </SheetHeader>

          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">{children}</div>

          {onDelete && (
            <DeleteFooter
              label={deleteLabel}
              isDeleting={isDeleting}
              isPending={isPending}
              onDelete={onDelete}
            />
          )}
        </SheetContent>
      </Sheet>
    );
  },
);
FormDrawer.displayName = 'FormDrawer';

export { FormDrawer };
